import { React, useState, useEffect } from 'react';
import getBackend from 'backend/backend';
import { useTranslation } from "react-i18next";
import { Table, Container, Alert, Button } from 'react-bootstrap';
import SidebarPage from 'components/sidebar_page';
import { Paths } from 'paths';
import { useNavigate } from 'react-router-dom';
import { showConfirmation } from 'components/misc';

const Workers = () => {
    const [t] = useTranslation('common');
    const [workers, setWorkers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updateSeqNo, setUpdateSeqNo] = useState(0);
    const navigate = useNavigate();
    const [stateFilter, setStateFilter] = useState('');
    const [modelFilter, setModelFilter] = useState('');
    const [hostnameFilter, setHostnameFilter] = useState('');

    useEffect(() => {
        const interval = setInterval(() => {
            setUpdateSeqNo(prev => prev + 1);
        }, 10000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        let canceled = false;

        const fetchWorkers = async () => {
            try {
                const result = await getBackend().getWorkers();
                if (!canceled && result.error == null) {
                    let workers = [];
                    for (const worker of result.response.workers) {
                        if (stateFilter !== '' && worker.state !== stateFilter)
                            continue;

                        if (modelFilter !== '' && !worker.model.includes(modelFilter))
                            continue;

                        if (hostnameFilter !== '' && !worker.hostname.includes(hostnameFilter))
                            continue;

                        workers.push(worker);
                    }
                    setWorkers(workers);
                } else if (!canceled) {
                    setError(result.error);
                }
            } catch (err) {
                if (!canceled) setError(err.message);
            } finally {
                if (!canceled) setLoading(false);
            }
        };

        fetchWorkers();

        return () => {
            canceled = true;
        };
    }, [updateSeqNo, stateFilter, modelFilter, hostnameFilter]);

    const handleDeleteWorker = async (workerId) => {
        setError(null);
        let result = await showConfirmation("Delete worker", "Are you really going to delete the worker?");
        if (result.isConfirmed) {
            const result = await getBackend().deleteWorker(workerId);
            if (result.error === null) {
                setUpdateSeqNo(updateSeqNo + 1);
            } else {
                setError(t('Error deleting worker'));
            }
        }
    };

    return (
        <SidebarPage title={t('Workers')} description={t('Workers')}>
            <Container className="my-5">

                <h2 className="mb-4">{t('Workers')}</h2>
                {loading &&
                    <div>{t('Loading...')}</div>
                }
                {error &&
                    <Alert variant="danger">{t('Error')}: {error}</Alert>
                }

                <div className="mb-3 d-flex gap-3">
                    <div>
                        <label htmlFor="stateFilter" className="form-label">{t('Filter by State')}</label>
                        <select 
                            id="stateFilter" 
                            className="form-select" 
                            onChange={(e) => setStateFilter(e.target.value)}
                        >
                            <option value="">{t('All')}</option>
                            <option value="running">{t('Running')}</option>
                            <option value="starting">{t('Starting')}</option>
                            <option value="stopping">{t('Stopping')}</option>
                            <option value="stopped">{t('Stopped')}</option>
                            <option value="deleting">{t('Deleting')}</option>
                            <option value="error">{t('Error')}</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="modelFilter" className="form-label">{t('Filter by Model')}</label>
                        <input 
                            type="text" 
                            id="modelFilter" 
                            className="form-control" 
                            placeholder={t('Enter model name')} 
                            onChange={(e) => setModelFilter(e.target.value)}
                        />
                    </div>
                    <div>
                        <label htmlFor="hostnameFilter" className="form-label">{t('Filter by Hostname')}</label>
                        <input 
                            type="text" 
                            id="hostnameFilter" 
                            className="form-control" 
                            placeholder={t('Enter hostname')} 
                            onChange={(e) => setHostnameFilter(e.target.value)}
                        />
                    </div>
                </div>

                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>{t('ID')}</th>
                            <th>{t('Hostname')}</th>
                            <th>{t('Model')}</th>
                            <th>{t('State')}</th>
                            <th>{t('Last Seen')}</th>
                            <th>{t('Last Error')}</th>
                            <th>{t('Actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {workers.map((worker) => (
                            <tr key={worker.id}>
                                <td>{worker.id}</td>
                                <td>{worker.hostname}</td>
                                <td>{worker.model}</td>
                                <td>{worker.state}</td>
                                <td>{worker.last_seen}</td>
                                <td>{worker.last_error}</td>
                                <td>
                                    <Button className="custom-button" variant="light" size="sm" onClick={() => {
                                        navigate(Paths.Worker.path + "/" + worker.id);
                                    }}>
                                        {t('Details')}
                                    </Button>
                                    <br />
                                    <Button
                                        className="my-2"
                                        variant="danger"
                                        size="sm"
                                        onClick={async () => {
                                            await handleDeleteWorker(worker.id);
                                        }}
                                    >
                                        {t('Delete')}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>

            </Container>
        </SidebarPage>
    );
};

export default Workers;
